import { useState, useEffect, useRef } from 'react'
import { Map, Marker, useMapsLibrary } from '@vis.gl/react-google-maps'
import { Input } from '@/components/ui/input'
import { MapPin, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { GoogleMapsProvider } from '@/components/maps/GoogleMapsProvider'
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID, RIYADH_CENTER } from '@/lib/googleMaps'

export interface LocationValue {
  address: string
  lat?: number
  lng?: number
  placeId?: string
}

interface LocationPickerProps {
  value?: LocationValue | string
  onChange: (value: LocationValue) => void
  placeholder?: string
  placeholderAr?: string
  isArabic?: boolean
  hasError?: boolean
  height?: string
}

// ─── Place Autocomplete (internal) ──────────────────────────────────────────
interface PlaceAutocompleteProps {
  onPlaceSelect: (location: LocationValue) => void
  placeholder: string
  hasError?: boolean
}

function PlaceAutocomplete({ onPlaceSelect, placeholder, hasError }: PlaceAutocompleteProps) {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const places = useMapsLibrary('places')

  useEffect(() => {
    if (!places || !inputRef.current) return

    const options: google.maps.places.AutocompleteOptions = {
      fields: ['geometry', 'name', 'formatted_address', 'place_id'],
      componentRestrictions: { country: 'sa' }, // Restrict to Saudi Arabia
    }
    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options))
  }, [places])

  useEffect(() => {
    if (!placeAutocomplete) return

    placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace()
      if (place.geometry?.location) {
        const location: LocationValue = {
          address: place.formatted_address || place.name || '',
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          placeId: place.place_id,
        }
        onPlaceSelect(location)
      }
    })
  }, [placeAutocomplete, onPlaceSelect])

  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
      <Input
        ref={inputRef}
        placeholder={placeholder}
        className={cn(
          'h-11 pl-10',
          hasError && 'border-destructive focus-visible:ring-destructive/20'
        )}
      />
    </div>
  )
}

// ─── Main LocationPicker ────────────────────────────────────────────────────
export function LocationPicker({
  value,
  onChange,
  placeholder = 'Search for a location...',
  placeholderAr = 'ابحث عن موقع...',
  isArabic = false,
  hasError = false,
  height = '350px',
}: LocationPickerProps) {
  const [selectedPlace, setSelectedPlace] = useState<LocationValue | null>(null)

  // Parse initial value
  useEffect(() => {
    if (!value) return
    if (typeof value === 'string') {
      setSelectedPlace({ address: value })
    } else {
      setSelectedPlace(value)
    }
  }, []) // only on mount

  const handlePlaceSelect = (location: LocationValue) => {
    setSelectedPlace(location)
    onChange(location)
  }

  const handleMapClick = (event: any) => {
    if (event.detail?.latLng) {
      const lat = event.detail.latLng.lat
      const lng = event.detail.latLng.lng
      const location: LocationValue = {
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        lat,
        lng,
      }
      setSelectedPlace(location)
      onChange(location)
    }
  }

  const mapCenter =
    selectedPlace?.lat && selectedPlace?.lng
      ? { lat: selectedPlace.lat, lng: selectedPlace.lng }
      : RIYADH_CENTER

  return (
    <GoogleMapsProvider>
      <div className="w-full space-y-2">
        {/* Warning only in production when API key is missing (CI injects key for deployed app) */}
        {!GOOGLE_MAPS_API_KEY && import.meta.env.MODE === 'production' && (
          <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-700">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <p className="text-xs">
              {isArabic
                ? 'مفتاح خرائط جوجل غير مُعيَّن. أضف VITE_GOOGLE_MAPS_API_KEY إلى ملف .env'
                : 'Google Maps API key not set. Add VITE_GOOGLE_MAPS_API_KEY to your .env file.'}
            </p>
          </div>
        )}

        {/* Search autocomplete */}
        <PlaceAutocomplete
          onPlaceSelect={handlePlaceSelect}
          placeholder={isArabic ? placeholderAr : placeholder}
          hasError={hasError}
        />

        {/* Inline embedded map */}
        <div
          style={{ height }}
          className={cn(
            'w-full border rounded-lg overflow-hidden',
            hasError && 'border-destructive'
          )}
        >
          <Map
                mapId={GOOGLE_MAPS_MAP_ID}
                defaultCenter={mapCenter}
                defaultZoom={12}
                gestureHandling="greedy"
                disableDefaultUI={false}
                onClick={handleMapClick}
              >
            {selectedPlace?.lat && selectedPlace?.lng && (
              <Marker
                position={{
                  lat: selectedPlace.lat,
                  lng: selectedPlace.lng,
                }}
              />
            )}
          </Map>
        </div>

        {/* Selected location info */}
        {selectedPlace?.address && (
          <div className="flex items-start gap-2 p-2.5 bg-primary/5 border border-primary/20 rounded-lg">
            <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{selectedPlace.address}</p>
              {selectedPlace.lat != null && selectedPlace.lng != null && (
                <p className="text-xs text-muted-foreground">
                  {selectedPlace.lat.toFixed(6)}, {selectedPlace.lng.toFixed(6)}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </GoogleMapsProvider>
  )
}
